<script setup lang="ts">
import { RouteEnum } from '@/constants/enums/RouteEnum';

const route = useRoute();
const appStore = useAppStore();
const userData = useUserStore();
const { loadingStatus } = storeToRefs(usePopStore());
const { openBulletin, hideToday, isBulletOpen: visibleBulletOpen } = useBulletin();

// 控制公告自動彈出的變數
let hasAutoOpenedBulletin = false;

const fnBulletClosePopup = (val: boolean) => {
  if (val) {
    visibleBulletOpen.value = false;
    hideToday();
  }
};

const handleGetCurrentDevice = (): void => {
  appStore.isMobile = window.innerWidth <= 1024;
};

// 監聽路由變化以控制公告彈出
watch(
  [visibleBulletOpen, () => route.name],
  ([isOpen, routeName], [prevIsOpen, prevRouteName]) => {
    const isIndex: boolean = routeName === RouteEnum.Index;

    // 判斷是否應該自動彈出公告
    const shouldAutoOpenBulletin: boolean =
      !isOpen &&
      isIndex &&
      userData.hasLoginCondition &&
      !!userData.isPayUser &&
      !userData.isV2OfficialUser;

    if (hasAutoOpenedBulletin) return;

    if (!shouldAutoOpenBulletin) {
      if (!userData.hasLoginCondition) {
        visibleBulletOpen.value = false;
      }
      return;
    }

    hasAutoOpenedBulletin = true;
    openBulletin();
  },
  { immediate: true }
);

onMounted(() => {
  appStore.handleDetectDevice();
  handleGetCurrentDevice();
  window.addEventListener('resize', handleGetCurrentDevice);

  const preload = localStorage.getItem('preload');
  window.addEventListener('vite:preloadError', () => {
    if (!preload) {
      localStorage.setItem('preload', JSON.stringify(true));
      setTimeout(() => {
        location.reload();
      }, 300);
    } else {
      localStorage.removeItem('preload');
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleGetCurrentDevice);
});
</script>

<template>
  <RouterView />

  <teleport to="body">
    <Loading v-if="loadingStatus"></Loading>
  </teleport>

  <BasePopup
    :zIndex="2020"
    :coverArea="'fixed'"
    v-model:showPopup="visibleBulletOpen"
    @close-popup="fnBulletClosePopup"
  >
    <Bulletin @close-popup="fnBulletClosePopup"></Bulletin>
  </BasePopup>
</template>

<style>
@import 'vue-draggable-resizable/style.css';
</style>

<style lang="scss">
body {
  @include scroll-style();
  color: $txt-color-grey-80;
  font-family: PingFangTC, 微軟正黑體, $font-family-others;
}

@keyframes loader {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

.page-main {
  padding-top: $header-h;
  padding-left: $sidebar-w;

  @include media-breakpoint-down(lg) {
    padding-left: inherit;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.grow-enter-active,
.grow-leave-active {
  transition: all 0.5s ease;
}

.grow-enter-from,
.grow-leave-to {
  opacity: 0;
  width: 0;
}

.grow-enter-to,
.grow-leave-from {
  opacity: 1;
  width: 24px;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

/* 從左到右進入 */
.slide-enter-from {
  transform: translateX(100%);
}

.slide-enter-to {
  transform: translateX(0);
}

/* 從左到右離開 */
.slide-leave-from {
  transform: translateX(0);
  // opacity: 0;
}

.slide-leave-to {
  transform: translateX(-100%);
  // opacity: 0;
}

.header-scroll-hidden {
  transform: translateY(-200px);
}

.el-message-box .error-el-msg-box-btn {
  background-color: #f74768 !important;
  border-color: #f74768 !important;
  color: #fff !important;
}

// 自訂 el-tooltip 樣式
.el-tooltip {
  &.is-dark {
    font-size: 14px;
    --el-popper-bg-color-dark: #{$color-grey-80} !important;
    --el-text-color-primary: #{$color-grey-80} !important;
  }
}
</style>
